import { useEffect } from 'react';
import { InteractionManager, Platform } from 'react-native';
import { KeyboardController, KeyboardEvents, } from 'react-native-keyboard-controller';
import { useMainStackNavigation } from 'utils/types/navigationV6';
// How long to wait for a transition to start before focusing anyway. A pushed
// screen started its transition 100-300 ms after mounting on the emulator, and
// phones can be slower.
export const ANDROID_TRANSITION_FALLBACK_DELAY = 500;
export const useAutoFocusInput = (inputRef, delay = 50) => {
    const navigation = useMainStackNavigation();
    useEffect(() => {
        const focus = () => {
            if (inputRef?.current) {
                if ('focus' in inputRef.current) {
                    inputRef?.current.focus();
                }
                else if ('getElement' in inputRef.current) {
                    inputRef?.current.getElement().focus();
                }
            }
        };
        const blur = () => {
            if (inputRef?.current) {
                if ('blur' in inputRef.current) {
                    inputRef?.current.blur();
                }
                else if ('getElement' in inputRef.current) {
                    inputRef?.current.getElement().blur();
                }
            }
        };
        if (Platform.OS === 'ios') {
            let timeout;
            const unsubscribe = navigation.addListener('transitionEnd', () => {
                timeout = setTimeout(() => {
                    InteractionManager.runAfterInteractions(() => {
                        focus();
                        unsubscribe?.();
                    });
                });
            });
            return () => {
                if (timeout !== undefined) {
                    clearTimeout(timeout);
                }
                unsubscribe?.();
            };
        }
        if (Platform.OS === 'android') {
            // A pushed screen's views are attached to the window only when its
            // transition starts. Focusing an input before that leaves the keyboard
            // down on the New Architecture: Android ignores showSoftInput() for a
            // view it does not serve, and keeps ignoring it until the input's focus
            // changes. So focus when the transition starts, or after a fallback
            // delay for an input mounted on a screen that has already appeared, and
            // focus again if the keyboard has not started to open by a delay after
            // the screen has appeared. Checking right at transitionEnd was too
            // early: a short transition (Feed's search) ends before Android reports
            // the keyboard opening, and the blur closed a keyboard on its way up.
            let keyboardShown = false;
            const showSubscription = KeyboardEvents.addListener('keyboardWillShow', () => {
                keyboardShown = true;
            });
            let retry;
            let focused = false;
            const focusOnce = () => {
                clearTimeout(fallback);
                if (!focused) {
                    focused = true;
                    focus();
                }
            };
            const fallback = setTimeout(focusOnce, ANDROID_TRANSITION_FALLBACK_DELAY);
            const unsubscribeStart = navigation.addListener('transitionStart', (e) => {
                if (!e.data.closing) {
                    focusOnce();
                }
            });
            const unsubscribeEnd = navigation.addListener('transitionEnd', (e) => {
                if (e.data.closing) {
                    return;
                }
                unsubscribeStart();
                unsubscribeEnd();
                if (!focused) {
                    focusOnce();
                    return;
                }
                retry = setTimeout(() => {
                    if (!keyboardShown &&
                        !KeyboardController.isVisible() &&
                        navigation.isFocused()) {
                        blur();
                        focus();
                    }
                }, ANDROID_TRANSITION_FALLBACK_DELAY);
            });
            return () => {
                showSubscription.remove();
                clearTimeout(retry);
                clearTimeout(fallback);
                unsubscribeStart();
                unsubscribeEnd();
            };
        }
        const timeout = setTimeout(() => {
            focus();
        }, delay);
        return () => {
            clearTimeout(timeout);
        };
    }, [delay, inputRef, navigation]);
};
export const focus = (inputRef) => {
    if (inputRef?.current) {
        if ('focus' in inputRef.current) {
            inputRef?.current.focus();
        }
        else if ('getElement' in inputRef.current) {
            inputRef?.current.getElement().focus();
        }
    }
};
export const blur = (inputRef) => {
    if (inputRef?.current) {
        if ('blur' in inputRef.current) {
            inputRef?.current.blur();
        }
        else if ('getElement' in inputRef.current) {
            inputRef?.current.getElement().blur();
        }
    }
};
